import { Injectable, signal, computed } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { BaseService } from '../../abstract/base.service';
import { Chemical } from '../data-models/chemical.model';
import { ChemicalSource } from '../data-models/chemical-source.model';
import { ApiListResponse } from '../response-models/api-list-response.model';
import { stringArrayOrEmpty } from '../utils/wire-value-utils';
import { ChemicalSearchResult } from '../response-models/chemical-search-result.model';

/** Search filters for `GET /chemicals`. The hub caps `limit` at 1000 here (a lookup list). */
interface ChemicalSearchOptions {
  category?: string;
  source?: ChemicalSource | string;
  page?: number;
  limit?: number;
}

/** `GET /chemicals` body: a list response with the tab-badge counts at the root. */
type ChemicalListBody = ApiListResponse<unknown> & { epaCount?: unknown; adnCount?: unknown };



/**
 * The system-wide chemical book: `/chemicals[/:id]` and `/chemicals/categories` (hub `user-chemicals-controller.ts`).
 * Read-only. All chemical data comes from the hub (there is no mock data); organization chemicals live in
 * `CustomChemicalService`.
 *
 * Searches never reject: a failure sets `error` (the hub's message) and resolves to an empty result.
 */
@Injectable({
  providedIn: 'root'
})
export class ChemicalService extends BaseService<Chemical> {
  // Signal-based state management
  private searchResultsSignal = signal<Chemical[]>([]);
  private isSearchingSignal = signal<boolean>(false);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private searchQuerySignal = signal<string>('');
  private categoriesSignal = signal<string[]>([]);
  private epaCountSignal = signal<number>(0);
  private adnCountSignal = signal<number>(0);
  private totalSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private totalPagesSignal = signal<number>(1);

  // Public computed signals
  readonly searchResults = computed(() => this.searchResultsSignal());
  readonly isSearching = computed(() => this.isSearchingSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly searchQuery = computed(() => this.searchQuerySignal());
  readonly categories = computed(() => this.categoriesSignal());
  readonly epaCount = computed(() => this.epaCountSignal());
  readonly adnCount = computed(() => this.adnCountSignal());
  readonly total = computed(() => this.totalSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly totalPages = computed(() => this.totalPagesSignal());

  constructor() {
    super('chemicals', Chemical);
    this.loadCategories();
  }

  /**
   * Load categories from API
   */
  private async loadCategories(): Promise<void> {
    try {
      const categories = await firstValueFrom(this.request('GET', { path: 'categories' }, data => stringArrayOrEmpty(data)));
      this.categoriesSignal.set(categories);
    } catch (error: unknown) {
      console.error('[ChemicalService] Error loading categories:', ApiError.fromHttpError(error).message);
    }
  }

  /**
   * Search chemicals and publish the page to the shared signals (the Chemical Book's state).
   * GET /chemicals?q=&category=&source=&page=1&limit=25
   */
  async searchChemicals(query?: string, options: ChemicalSearchOptions = {}): Promise<ChemicalSearchResult> {
    this.isSearchingSignal.set(true);
    this.errorSignal.set(null);
    if (query) {
      this.searchQuerySignal.set(query);
    }

    try {
      const result = await firstValueFrom(this.search(query, options));
      this.searchResultsSignal.set(result.chemicals);
      this.totalSignal.set(result.total);
      this.currentPageSignal.set(result.page);
      this.totalPagesSignal.set(result.totalPages);
      // The counts only come with unfiltered loads
      if (result.epaCount !== undefined) {
        this.epaCountSignal.set(result.epaCount);
      }
      if (result.adnCount !== undefined) {
        this.adnCountSignal.set(result.adnCount);
      }
      return result;
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? 'Failed to search chemicals';
      console.error('[ChemicalService] Error searching chemicals:', message);
      this.errorSignal.set(message);
      return emptyResult();
    } finally {
      this.isSearchingSignal.set(false);
    }
  }

  /**
   * Search chemicals without updating shared signals.
   * Use this for independent lookups (e.g., template form modal)
   * that should not affect the Chemical Book's state.
   */
  async searchChemicalsQuiet(query?: string, options: ChemicalSearchOptions = {}): Promise<ChemicalSearchResult> {
    try {
      return await firstValueFrom(this.search(query, options));
    } catch (error: unknown) {
      console.error('[ChemicalService] Error searching chemicals:', ApiError.fromHttpError(error).message);
      return emptyResult();
    }
  }

  /**
   * Get a chemical by ID from API
   */
  async getChemicalById(id: string): Promise<Chemical | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      return await firstValueFrom(this.get(id));
    } catch (error: unknown) {
      const message = ApiError.fromHttpError(error).errors[0] ?? 'Failed to fetch chemical';
      console.error('[ChemicalService] Error fetching chemical:', message);
      this.errorSignal.set(message);
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get chemicals filtered by source (client-side from search results)
   */
  getChemicalsBySource(source: ChemicalSource): Chemical[] {
    return this.searchResultsSignal().filter(c => c.source === source);
  }

  /**
   * Get featured ADN-recommended chemicals
   */
  getFeaturedChemicals(): Chemical[] {
    return this.searchResultsSignal().filter(c => c.isFeatured === true);
  }

  /**
   * Clear search results
   */
  clearSearch(): void {
    this.searchQuerySignal.set('');
    this.searchResultsSignal.set([]);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /** One page of `GET /chemicals`, with the tab-badge counts the hub sends beside `paging`. */
  private search(query: string | undefined, options: ChemicalSearchOptions): Observable<ChemicalSearchResult> {
    const opts = {
      query: {
        q: query?.trim() || undefined,
        category: options.category || undefined,
        source: options.source || undefined,
        page: options.page || 1,
        limit: options.limit || 25,
      },
    };
    return this.requestEnvelope('GET', opts, body => this.toSearchResult(body as ChemicalListBody | null));
  }

  private toSearchResult(body: ChemicalListBody | null): ChemicalSearchResult {
    const chemicals = Array.isArray(body?.data) ? body.data.map(item => this.createType(item)) : [];
    const paging = body?.paging;
    const page = paging?.page ?? 1;
    const totalPages = paging?.totalPages ?? 1;
    return {
      chemicals,
      total: paging?.totalItems ?? chemicals.length,
      hasMore: page < totalPages,
      page,
      totalPages,
      epaCount: optionalCount(body?.epaCount),
      adnCount: optionalCount(body?.adnCount),
    };
  }

  /**
   * Get category badge class for UI
   */
  getCategoryBadgeClass(category: string | null | undefined): string {
    switch (category) {
      case 'herbicide':
        return 'bg-success-subtle text-success';
      case 'insecticide':
        return 'bg-danger-subtle text-danger';
      case 'fungicide':
        return 'bg-primary-subtle text-primary';
      case 'fertilizer':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  /**
   * Get source badge class for UI
   */
  getSourceBadgeClass(source: string | null | undefined): string {
    switch (source) {
      case 'epa':
        return 'bg-primary-subtle text-primary';
      case 'adn-recommended':
        return 'bg-info-subtle text-info';
      case 'custom':
        return 'bg-warning-subtle text-warning-emphasis';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  /**
   * Get source display label
   */
  getSourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'epa':
        return 'EPA';
      case 'adn-recommended':
        return 'AA';
      case 'custom':
        return 'Custom';
      default:
        return source ?? '';
    }
  }
}

function emptyResult(): ChemicalSearchResult {
  return { chemicals: [], total: 0, hasMore: false, page: 1, totalPages: 1 };
}

function optionalCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
